import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { 
  Database, 
  Download, 
  Upload, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  RefreshCw,
  Shield,
  Clock,
  HardDrive,
  FileCheck,
  Loader2,
  Info
} from 'lucide-react';

const BackupManagementPage = () => {
  const { API, user } = useContext(AuthContext);
  const [backups, setBackups] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [restoreStep, setRestoreStep] = useState(0); // 0: none, 1: select, 2: confirm
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [restoring, setRestoring] = useState(false);

  // Daten laden
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [backupsRes, statusRes] = await Promise.all([
        axios.get(`${API}/backup/list`),
        axios.get(`${API}/backup/status`)
      ]);
      setBackups(backupsRes.data.backups || []);
      setStatus(statusRes.data.status);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      toast.error('Fehler beim Laden der Backup-Daten');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Backup erstellen
  const handleCreateBackup = async () => {
    try {
      setCreating(true);
      toast.info('Backup wird erstellt...');
      
      const response = await axios.post(`${API}/backup/create`, {});
      
      if (response.data.success) {
        toast.success(`Backup erfolgreich erstellt: ${response.data.backup.filename}`);
        loadData();
      } else {
        toast.error('Backup konnte nicht erstellt werden');
      }
    } catch (error) {
      console.error('Fehler beim Erstellen:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Backups');
    } finally {
      setCreating(false);
    }
  };

  // Backup verifizieren
  const handleVerifyBackup = async (filename) => {
    try {
      setVerifying(filename);
      const response = await axios.get(`${API}/backup/verify/${filename}`);
      
      if (response.data.valid) {
        toast.success(`Backup "${filename}" ist gültig und intakt`);
      } else {
        toast.error(`Backup "${filename}" ist beschädigt: ${response.data.details?.error}`);
      }
      
      loadData();
    } catch (error) {
      console.error('Fehler bei Verifizierung:', error);
      toast.error(error.response?.data?.detail || 'Fehler bei der Verifizierung');
    } finally {
      setVerifying(null);
    }
  };

  // Restore starten (Schritt 1)
  const handleRestoreStart = (backup) => {
    setSelectedBackup(backup);
    setRestoreStep(1);
    setConfirmText('');
  };

  // Restore abbrechen
  const handleRestoreCancel = () => {
    setRestoreStep(0);
    setSelectedBackup(null);
    setConfirmText('');
  };

  // Restore bestätigen (Schritt 2)
  const handleRestoreConfirm = () => {
    setRestoreStep(2);
  };

  // Restore ausführen
  const handleRestoreExecute = async () => {
    if (confirmText !== 'RESTORE') {
      toast.error('Bitte geben Sie "RESTORE" ein, um fortzufahren');
      return;
    }

    try {
      setRestoring(true);
      toast.info('Wiederherstellung wird durchgeführt...');
      
      const response = await axios.post(`${API}/backup/restore/${selectedBackup.filename}`, {
        confirm: true
      });
      
      if (response.data.success) {
        toast.success('Backup erfolgreich wiederhergestellt!');
        handleRestoreCancel();
        loadData();
      } else {
        toast.error('Wiederherstellung fehlgeschlagen');
      }
    } catch (error) {
      console.error('Fehler bei Restore:', error);
      toast.error(error.response?.data?.detail || 'Fehler bei der Wiederherstellung');
    } finally {
      setRestoring(false);
    }
  };

  // Backup-Typ Badge
  const getTypeBadge = (type) => {
    const badges = {
      'daily': { label: 'Täglich', color: 'bg-blue-100 text-blue-800' },
      'pre_deployment': { label: 'Pre-Deploy', color: 'bg-purple-100 text-purple-800' },
      'manual': { label: 'Manuell', color: 'bg-green-100 text-green-800' }
    };
    const badge = badges[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  // Datum formatieren
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Database className="h-6 w-6 text-blue-500" />
            Backup-Verwaltung
          </h1>
          <p className="text-slate-500 mt-1">
            Datenbank-Backups verwalten und wiederherstellen
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={loadData}
            variant="outline"
            size="sm"
            disabled={loading}
            data-testid="refresh-backups-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
          <Button
            onClick={handleCreateBackup}
            disabled={creating}
            data-testid="create-backup-btn"
          >
            {creating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Backup erstellen
          </Button>
        </div>
      </div>

      {/* Status-Karten */}
      {status && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="status-total-backups">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <HardDrive className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{status.total_backups}</p>
                  <p className="text-sm text-slate-500">Backups gesamt</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="status-storage">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Database className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{status.total_size_mb} MB</p>
                  <p className="text-sm text-slate-500">Speicherverbrauch</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="status-schedule">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{status.schedule?.daily_backup}</p>
                  <p className="text-sm text-slate-500">Tägliches Backup</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="status-retention">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Shield className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">Max. 10</p>
                  <p className="text-sm text-slate-500">Retention Policy</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Warnung */}
      <Card className="border-amber-200 bg-amber-50" data-testid="restore-warning">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Wichtiger Hinweis</p>
              <p className="text-sm text-amber-700 mt-1">
                Das Wiederherstellen eines Backups überschreibt <strong>alle aktuellen Live-Daten</strong>. 
                Nutzen Sie diese Funktion nur im Notfall, z.B. nach einem fehlgeschlagenen Update.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Backup-Liste */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCheck className="h-5 w-5" />
            Verfügbare Backups
          </CardTitle>
          <CardDescription>
            {backups.length} Backup(s) verfügbar
          </CardDescription>
        </CardHeader>
        <CardContent>
          {backups.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Database className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Noch keine Backups vorhanden</p>
              <p className="text-sm mt-1">Erstellen Sie jetzt Ihr erstes Backup</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="backups-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Dateiname</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Typ</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Datum/Zeit</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Version</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Größe</th>
                    <th className="text-left py-3 px-2 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-right py-3 px-2 text-sm font-medium text-slate-600">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((backup, index) => (
                    <tr 
                      key={backup.filename} 
                      className={`border-b border-slate-100 hover:bg-slate-50 ${backup.protected ? 'bg-purple-50' : ''}`}
                      data-testid={`backup-row-${index}`}
                    >
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          {backup.protected && (
                            <Shield className="h-4 w-4 text-purple-500" title="Geschützt" />
                          )}
                          <span className="font-mono text-sm truncate max-w-[200px]" title={backup.filename}>
                            {backup.filename}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        {getTypeBadge(backup.backup_type)}
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-600">
                        {formatDate(backup.created_at)}
                      </td>
                      <td className="py-3 px-2 text-sm">
                        <span className="bg-slate-100 px-2 py-0.5 rounded text-slate-700">
                          v{backup.app_version}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-sm text-slate-600">
                        {backup.file_size_mb} MB
                      </td>
                      <td className="py-3 px-2">
                        <CheckCircle className="h-4 w-4 text-green-500" title="OK" />
                      </td>
                      <td className="py-3 px-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleVerifyBackup(backup.filename)}
                            disabled={verifying === backup.filename}
                            data-testid={`verify-btn-${index}`}
                          >
                            {verifying === backup.filename ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <FileCheck className="h-4 w-4" />
                            )}
                            <span className="ml-1 hidden sm:inline">Prüfen</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreStart(backup)}
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            data-testid={`restore-btn-${index}`}
                          >
                            <Upload className="h-4 w-4" />
                            <span className="ml-1 hidden sm:inline">Restore</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restore Dialog - Schritt 1: Auswahl */}
      {restoreStep === 1 && selectedBackup && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="restore-modal-step1">
          <Card className="w-full max-w-lg">
            <CardHeader className="border-b border-red-200 bg-red-50">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                Backup wiederherstellen
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="bg-red-100 border border-red-300 rounded-lg p-4">
                <p className="font-bold text-red-800 text-lg">⚠️ WARNUNG</p>
                <p className="text-red-700 mt-2">
                  Das Wiederherstellen dieses Backups <strong>überschreibt alle aktuellen Live-Daten</strong>. 
                  Diese Aktion kann NICHT rückgängig gemacht werden.
                </p>
                <p className="text-red-700 mt-2">
                  Nur im Notfall verwenden!
                </p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="font-medium">Ausgewähltes Backup:</p>
                <div className="text-sm space-y-1">
                  <p><span className="text-slate-500">Datei:</span> {selectedBackup.filename}</p>
                  <p><span className="text-slate-500">Erstellt:</span> {formatDate(selectedBackup.created_at)}</p>
                  <p><span className="text-slate-500">Version:</span> v{selectedBackup.app_version}</p>
                  <p><span className="text-slate-500">Größe:</span> {selectedBackup.file_size_mb} MB</p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={handleRestoreCancel}
                  data-testid="restore-cancel-btn"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleRestoreConfirm}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="restore-continue-btn"
                >
                  Weiter zur Bestätigung
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Restore Dialog - Schritt 2: Bestätigung */}
      {restoreStep === 2 && selectedBackup && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="restore-modal-step2">
          <Card className="w-full max-w-lg">
            <CardHeader className="border-b border-red-200 bg-red-50">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                Letzte Bestätigung erforderlich
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="bg-red-100 border border-red-300 rounded-lg p-4">
                <p className="font-bold text-red-800">
                  ⚠️ ACHTUNG: Alle Live-Daten werden überschrieben!
                </p>
                <p className="text-red-700 mt-2 text-sm">
                  Diese Aktion ist endgültig. Es gibt keine Möglichkeit, die aktuellen Daten 
                  nach dem Restore wiederherzustellen.
                </p>
              </div>

              <div className="space-y-3">
                <p className="font-medium">
                  Geben Sie <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">RESTORE</span> ein, 
                  um die Wiederherstellung zu bestätigen:
                </p>
                <Input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  placeholder="RESTORE eingeben"
                  className="font-mono text-center text-lg"
                  data-testid="restore-confirm-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={handleRestoreCancel}
                  disabled={restoring}
                  data-testid="restore-back-btn"
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleRestoreExecute}
                  disabled={confirmText !== 'RESTORE' || restoring}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-red-300"
                  data-testid="restore-execute-btn"
                >
                  {restoring ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Wird wiederhergestellt...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Backup JETZT wiederherstellen
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info-Bereich */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Info className="h-4 w-4" />
            Backup-Informationen
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-2">
          <p>• <strong>Tägliche Backups</strong> werden automatisch um 00:00 UTC erstellt</p>
          <p>• <strong>Pre-Deployment Backups</strong> werden vor Updates erstellt und sind geschützt</p>
          <p>• <strong>Manuelle Backups</strong> können jederzeit über "Backup erstellen" angelegt werden</p>
          <p>• <strong>Retention Policy:</strong> Es werden maximal 10 Backups aufbewahrt</p>
          <p>• <strong>Verschlüsselung:</strong> Alle Backups sind mit AES-256 verschlüsselt</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default BackupManagementPage;
